import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { AuditFindingsTable } from '@/components/shared/AuditFindingsTable';

/**
 * A network company's audit findings. Same component as the own-company
 * audits findings tab (`audits/(pages)/(detail)/$auditId/findings`): the table
 * derives own-vs-network from `companyId` and picks the matching findings
 * endpoint itself.
 *
 * This route used to carry a 287-line local copy of that table with its own
 * create-plan / add-finding dialogs, both of which posted bodies the backend
 * rejects (capa batches 4–8 — see `modules/capa/docs/migrate-capa-to-modules.md`).
 */

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
  sort: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/network/(pages)/(detail)/$companyId/audits/$auditId/findings/',
)({
  validateSearch: searchSchema,
  component: NetworkAuditFindingsPage,
});

function NetworkAuditFindingsPage() {
  const { companyId, auditId } = Route.useParams();
  const search = Route.useSearch();

  // Best-effort only: the names label the locked Source and Company in the
  // create dialogs. `GET /api/audits/:id` is scoped to audits the caller was
  // granted, so for a network audit it can 403/404 — the table renders
  // regardless, keyed on the route's own `companyId`, exactly as before.
  const { data: audit } = useAuditDetail(auditId);

  return (
    <AuditFindingsTable
      companyId={companyId}
      auditId={auditId}
      {...(audit?.name != null ? { auditName: audit.name } : {})}
      {...(audit?.companyName != null
        ? { companyName: audit.companyName }
        : {})}
      page={search.page ?? 1}
      {...(search.sort !== undefined ? { sort: search.sort } : {})}
    />
  );
}
